import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const BookingConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showConfetti, setShowConfetti] = useState(true);
  
  // Get confirmation data from checkout
  const confirmationData = location.state || {
    type: 'course',
    orderNumber: 'DF-ABC123XYZ',
    item: {
      title: 'Intuitive Development Mastery',
      instructor: 'Sarah Moonweaver',
      price: 299
    }
  };

  const isSession = confirmationData.type === 'session';
  
  // Sample session details if it's a booking
  const sessionDetails = isSession ? {
    practitioner: confirmationData.practitioner || { 
      name: 'Sarah Moon', 
      email: 'sarah@divineframeworks.com',
      image: '👩‍🦰' 
    },
    service: confirmationData.service || { 
      title: '60-Minute Deep Dive Session', 
      duration: 60 
    },
    date: confirmationData.date || new Date(),
    time: confirmationData.time || '2:00 PM',
    meetingLink: 'https://zoom.us/j/123456789'
  } : null;

  useEffect(() => {
    // Hide confetti after 3 seconds
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleCalendarDownload = () => {
    // Would generate .ics file for calendar
    alert('Calendar event downloaded!');
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          <div className="absolute top-0 left-1/4 text-4xl animate-bounce">🎉</div>
          <div className="absolute top-0 right-1/4 text-4xl animate-bounce delay-100">✨</div>
          <div className="absolute top-0 left-1/2 text-4xl animate-bounce delay-200">🎊</div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Success Message */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">
            {isSession ? 'Booking Confirmed!' : 'Purchase Successful!'}
          </h1>
          <p className="text-lg text-slate-600">
            Order #{confirmationData.orderNumber}
          </p>
        </div>

        {/* Main Confirmation Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden mb-8">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
            <h2 className="text-2xl font-bold mb-2">
              {isSession ? 'Your Session is Booked' : 'Welcome to Your Course!'}
            </h2>
            <p className="text-blue-100">
              {isSession 
                ? 'Check your email for session details and meeting link'
                : 'You now have full access to all course materials'
              }
            </p>
          </div>

          {/* Content */}
          <div className="p-6">
            {isSession ? (
              // Session Booking Details
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="h-16 w-16 bg-gradient-to-br from-slate-200 to-slate-300 rounded-full flex items-center justify-center text-2xl">
                    {sessionDetails.practitioner.image}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900">
                      {sessionDetails.service.title}
                    </h3>
                    <p className="text-slate-600">with {sessionDetails.practitioner.name}</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-slate-500 mb-1">Date & Time</p>
                      <p className="font-semibold text-slate-900">
                        {sessionDetails.date.toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>
                      <p className="text-slate-700">{sessionDetails.time} (60 minutes)</p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-slate-500 mb-1">Session Type</p>
                      <p className="font-semibold text-slate-900">Online Video Call</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-slate-500 mb-1">Meeting Link</p>
                      <a href={sessionDetails.meetingLink} className="text-blue-600 hover:text-blue-800 font-medium">
                        Join via Zoom →
                      </a>
                      <p className="text-sm text-slate-600 mt-1">Link will also be sent via email</p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-slate-500 mb-1">Practitioner Contact</p>
                      <p className="text-slate-700">{sessionDetails.practitioner.email}</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-200">
                  <button
                    onClick={handleCalendarDownload}
                    className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-lg font-medium hover:bg-slate-800"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Add to Calendar
                  </button>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700"
                  >
                    View in Dashboard
                  </button>
                  <button
                    onClick={() => navigate(`/practitioner/${sessionDetails.practitioner.id}`)}
                    className="border border-slate-300 text-slate-700 px-4 py-2 rounded-lg font-medium hover:bg-slate-50"
                  >
                    Message Practitioner
                  </button>
                </div>

                {/* Important Reminders */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-2">Session Reminders</h4>
                  <ul className="space-y-1 text-sm text-blue-800">
                    <li>• You'll receive an email reminder 24 hours before your session</li>
                    <li>• Please join the session 5 minutes early to test your connection</li>
                    <li>• Have any questions or topics ready to discuss</li>
                    <li>• Find a quiet, private space for your session</li>
                  </ul>
                </div>
              </div>
            ) : (
              // Course Purchase Details
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="h-16 w-16 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center text-2xl text-white">
                    📚
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900">
                      {confirmationData.item.title}
                    </h3>
                    <p className="text-slate-600">by {confirmationData.item.instructor}</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-medium text-slate-500 mb-1">Access Type</p>
                    <p className="font-semibold text-slate-900">Lifetime Access</p>
                    <p className="text-sm text-slate-600">Learn at your own pace</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500 mb-1">Certificate</p>
                    <p className="font-semibold text-slate-900">Available Upon Completion</p>
                    <p className="text-sm text-slate-600">Complete all modules to earn</p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-200">
                  <button
                    onClick={() => navigate(`/learn/course/${confirmationData.item.id || 'intuitive-mastery'}/lesson/intro-1`)}
                    className="bg-slate-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-slate-800 hover:shadow-lg hover:-translate-y-0.5 transition-all"
                  >
                    Start Learning Now →
                  </button>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="border border-slate-300 text-slate-700 px-6 py-3 rounded-lg font-semibold hover:bg-slate-50"
                  >
                    Go to Dashboard
                  </button>
                </div>

                {/* Getting Started Tips */}
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h4 className="font-semibold text-green-900 mb-2">Getting Started Tips</h4>
                  <ul className="space-y-1 text-sm text-green-800">
                    <li>• Watch the welcome video first for course overview</li>
                    <li>• Download the course workbook and resources</li>
                    <li>• Join our student community for support</li>
                    <li>• Set aside 30-45 minutes daily for best results</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Receipt Section */}
        <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Payment Receipt</h3>
            <button
              onClick={handlePrintReceipt}
              className="text-blue-600 hover:text-blue-800 font-medium text-sm"
            >
              Print Receipt
            </button>
          </div>

          <div className="space-y-3 text-sm">
            <div className="flex justify-between py-2 border-b border-slate-200">
              <span className="text-slate-600">Order Number</span>
              <span className="font-medium text-slate-900">{confirmationData.orderNumber}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-200">
              <span className="text-slate-600">Date</span>
              <span className="font-medium text-slate-900">{new Date().toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-200">
              <span className="text-slate-600">Payment Method</span>
              <span className="font-medium text-slate-900">•••• 4242</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-200">
              <span className="text-slate-600">
                {isSession ? 'Session' : 'Course'}
              </span>
              <span className="font-medium text-slate-900">
                {isSession ? sessionDetails.service.title : confirmationData.item.title}
              </span>
            </div>
            <div className="flex justify-between pt-2">
              <span className="font-semibold text-slate-900">Total Paid</span>
              <span className="font-bold text-lg text-slate-900">
                ${(isSession ? sessionDetails.service.price : confirmationData.item.price) || 299}
              </span>
            </div>
          </div>

          <p className="text-xs text-slate-500 mt-4">
            A copy of this receipt has been sent to your email address. For any questions, please contact support@divineframeworks.com
          </p>
        </div>

        {/* Additional Actions */}
        <div className="mt-8 text-center">
          <p className="text-slate-600 mb-4">
            {isSession 
              ? 'Need to make changes to your booking?'
              : 'Want to explore more courses?'
            }
          </p>
          <div className="flex justify-center gap-4">
            {isSession ? (
              <>
                <button
                  onClick={() => navigate('/directory')}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  Book Another Session
                </button>
                <span className="text-slate-400">•</span>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  Manage Bookings
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/learn')}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  Browse More Courses
                </button>
                <span className="text-slate-400">•</span>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  View My Courses
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;